import { useEffect, useRef } from "react";
import { LogFirebase } from "../../services/firestore";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY } from "../../constants/FirebaseConstants";

/**
 * Hook to monitor CPU pressure using the Compute Pressure API
 * Logs CPU pressure state to Firebase for telemetry
 * Note: Limited browser support (Chrome 125+, Edge 125+)
 * On unsupported browsers, logs "not supported" once
 */
const useCPUPressure = () => {
    const hasLoggedNotSupported = useRef(false);

    useEffect(() => {
        // Check if Compute Pressure API is supported
        if (!("PressureObserver" in window)) {
            // Only log "not supported" once per session
            if (!hasLoggedNotSupported.current) {
                LogFirebase(FIREBASE_EVENT_CATEGORY.TwilioTelemetry, FIREBASE_LOG_KEY.CPUPressureNotSupported, {
                    reason: "PressureObserver API not available",
                    userAgent: navigator.userAgent,
                });
                hasLoggedNotSupported.current = true;
            }
            return;
        }

        let observer;

        try {
            observer = new window.PressureObserver((records) => {
                const latestRecord = records[records.length - 1];
                // Only log when state changes to non-nominal (avoid flooding logs)
                if (latestRecord.state !== "nominal") {
                    LogFirebase(FIREBASE_EVENT_CATEGORY.TwilioTelemetry, FIREBASE_LOG_KEY.CPUPressure, {
                        state: latestRecord.state, // "nominal", "fair", "serious", or "critical"
                        timestamp: latestRecord.time,
                    });
                }
            });

            // Sample every 5 seconds to avoid excessive logging
            observer.observe("cpu", { sampleInterval: 10000 });
        } catch (error) {
            // Log error if observer creation fails
            LogFirebase(FIREBASE_EVENT_CATEGORY.TwilioTelemetry, FIREBASE_LOG_KEY.CPUPressureNotSupported, {
                reason: "PressureObserver creation failed",
                error: error?.message,
            });
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, []);
};

export default useCPUPressure;
